import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { fn } from '../api/functions';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { AudioRecorder } from '../components/AudioRecorder';
import { AnswerThread } from '../components/AnswerThread';
import { StoryHeader } from '../components/StoryHeader';

export const CollaborativeStoryEditorPage = () => {
  const { storyId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [story, setStory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState({}); // Map of questionId -> array of answers
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [userRole, setUserRole] = useState(null);
  const [currentAuthor, setCurrentAuthor] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null); // For accordion expand/collapse
  const [isRecording, setIsRecording] = useState(false);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [presenceInterval, setPresenceInterval] = useState(null);
  const [lastPresenceUpdate, setLastPresenceUpdate] = useState(null);
  const [answerUnsubscribers, setAnswerUnsubscribers] = useState([]);

  // Helper function to acknowledge presence with UI feedback
  const acknowledgePresence = async (context = 'manual') => {
    try {
      // TODO: Re-enable when function is deployed
      // await fn.ackCollaboratorPresence({ storyId });
      console.log(`⏸️ Skipping ackCollaboratorPresence (not yet deployed) - context: ${context}`);
      setLastPresenceUpdate(new Date());
      return true;
    } catch (error) {
      console.error(`Error acknowledging presence (${context}):`, error);
      return false;
    }
  };

  // Periodic presence acknowledgment for active collaboration
  useEffect(() => {
    if (!storyId || !currentUser || userRole !== 'author') return;

    // Set up periodic presence acknowledgment (every 5 minutes)
    const interval = setInterval(async () => {
      await acknowledgePresence('periodic');
    }, 5 * 60 * 1000); // 5 minutes

    setPresenceInterval(interval);

    // Cleanup on unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [storyId, currentUser, userRole]);

  // Real-time story listener
  useEffect(() => {
    if (!storyId || !currentUser) return;

    const storyRef = doc(db, 'stories', storyId);
    const unsubscribe = onSnapshot(storyRef, async (doc) => {
      if (doc.exists()) {
        const storyData = { id: doc.id, ...doc.data() };
        setStory(storyData);
        
        // Determine user's role and author info
        // Check multiple sources for access:
        // 1. authors array (for accepted collaborators)
        // 2. primaryAuthor (for story creator)
        // 3. collaborators array (for pending/active collaborators)
        const author = storyData.authors?.find(a => 
          a.email === currentUser.email || a.userId === currentUser.uid
        );
        
        const isPrimaryAuthor = (storyData.primaryAuthor?.userId === currentUser.uid) || 
                                 (storyData.primaryAuthor?.email === currentUser.email);
        
        const collaborator = storyData.collaborators?.find(c => 
          (c.email && c.email.toLowerCase() === currentUser.email?.toLowerCase()) ||
          (c.userId === currentUser.uid)
        );
        
        if (author || isPrimaryAuthor || collaborator) {
          setUserRole('author');
          // Use existing author object or create one from primaryAuthor or collaborator
          setCurrentAuthor(author || {
            userId: collaborator?.userId || currentUser.uid,
            email: collaborator?.email || currentUser.email,
            name: collaborator?.name || storyData.primaryAuthor?.name || currentUser.displayName || 'Author',
            joinedAt: collaborator?.joinedAt || new Date(),
            status: collaborator?.status || 'active'
          });
          
          // If user is a pending collaborator, acknowledge presence to activate them
          if (collaborator && collaborator.status === 'pending') {
            try {
              console.log('👋 Acknowledging presence for pending collaborator...');
              const result = await acknowledgePresence('pending-collaborator-activation');
              if (result) {
                console.log('✅ Collaborator activated successfully!');
              }
            } catch (error) {
              console.error('Error activating collaborator:', error);
            }
          } else if (author && (!author.userId || !author.joinedAt)) {
            // Join story if user hasn't joined yet (only for collaborators in authors array)
            try {
              await fn.joinCollaborativeStory({ storyId });
              console.log('✅ Successfully joined collaborative story');
              
              // Acknowledge collaborator presence for collaborative stories
              await acknowledgePresence('initial-join');
            } catch (error) {
              console.error('Error joining story:', error);
            }
          } else if (author || collaborator) {
            // For users who have already joined, still acknowledge presence on page load
            await acknowledgePresence('page-load');
          } else {
            // Primary author (not in authors array yet) - just acknowledge presence
            await acknowledgePresence('primary-author-load');
          }
        } else if (storyData.giftInfo?.giftGiverEmail === currentUser.email) {
          setUserRole('gift_giver');
        } else {
          setError('You do not have access to this collaborative story');
          return;
        }
        
        setLoading(false);
      } else {
        setError('Collaborative story not found');
        setLoading(false);
      }
    }, (error) => {
      console.error('Error loading collaborative story:', error);
      
      // If it's a permissions error on initial load, it might be a timing issue
      // The story may have just been created and Firestore rules haven't propagated yet
      if (error.code === 'permission-denied' && !story && retryCount < 5) {
        console.log(`⏳ Permission denied on initial load (attempt ${retryCount + 1}/5) - retrying in ${Math.pow(2, retryCount)}s...`);
        setError(`Loading collaborative story... (attempt ${retryCount + 1}/5)`);
        
        // Retry with exponential backoff: 1s, 2s, 4s, 8s, 16s
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          console.log('🔄 Retrying story load...');
          setRetryCount(prev => prev + 1);
        }, delay);
      } else if (error.code === 'permission-denied') {
        setError('Permission denied. Please make sure you have access to this story.');
        setLoading(false);
      } else {
        setError('Failed to load story');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [storyId, currentUser, retryCount]);

  // Real-time questions listener
  useEffect(() => {
    if (!storyId) return;

    console.log('👂 Setting up questions listener for story:', storyId);
    const questionsRef = collection(db, 'stories', storyId, 'questions');
    const questionsQuery = query(questionsRef, orderBy('order', 'asc'));
    
    const unsubscribe = onSnapshot(questionsQuery, (snapshot) => {
      console.log(`📥 Received ${snapshot.docs.length} questions from Firestore`);
      const questionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('📋 Questions data:', questionsData);
      setQuestions(questionsData);
      
      // IMMEDIATELY set up answers listeners for each question
      if (questionsData.length > 0) {
        console.log('👂 INLINE: Setting up answers listeners for', questionsData.length, 'questions');
        
        // Clean up previous answer listeners
        answerUnsubscribers.forEach(unsub => {
          try {
            unsub();
          } catch (e) {
            console.error('Error unsubscribing:', e);
          }
        });
        
        // Clear any existing answer state
        setQuestionAnswers({});
        
        const newUnsubscribers = [];
        
        questionsData.forEach((question) => {
          const answersRef = collection(db, 'stories', storyId, 'questions', question.id, 'answers');
          
          console.log(`🎯 INLINE: Setting up listener for question ${question.id.substring(0, 8)}...`);
          
          const unsubscribe = onSnapshot(answersRef, (answerSnapshot) => {
            const answers = answerSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            console.log(`📥 INLINE: Question ${question.id.substring(0, 8)}... has ${answers.length} answers:`, answers.map(a => ({ 
              id: a.id.substring(0, 8), 
              authorEmail: a.authorEmail || a.userEmail,
              hasTranscript: !!(a.rawTranscription || a.cleanedTranscription)
            })));
            
            setQuestionAnswers(prev => {
              const updated = {
                ...prev,
                [question.id]: answers
              };
              console.log(`💾 INLINE: Updated questionAnswers state for ${question.id.substring(0, 8)}:`, updated[question.id]?.length || 0, 'answers');
              return updated;
            });
          }, (error) => {
            console.error(`❌ INLINE: Error loading answers for question ${question.id}:`, error);
          });
          
          newUnsubscribers.push(unsubscribe);
        });
        
        setAnswerUnsubscribers(newUnsubscribers);
      }
    }, (error) => {
      console.error('❌ Error loading questions:', error);
    });

    return () => {
      unsubscribe();
      // Clean up answer listeners on unmount
      answerUnsubscribers.forEach(unsub => {
        try {
          unsub();
        } catch (e) {
          console.error('Error unsubscribing on unmount:', e);
        }
      });
    };
  }, [storyId]);

  // Generate questions if story is ready but has no questions
  useEffect(() => {
    if (story && questions.length === 0 && !generatingQuestions) {
      console.log('📝 Auto-generating questions for collaborative story...', { storyId: story.id });
      handleGenerateQuestions();
    }
  }, [story, questions]);

  const handleGenerateQuestions = async () => {
    if (!story || generatingQuestions) return;
    
    setGeneratingQuestions(true);
    try {
      console.log('🤖 Calling generateCollaborativeQuestions function...', { storyId: story.id });
      const result = await fn.generateCollaborativeQuestions({ storyId: story.id });
      
      console.log('📬 Got result from generateCollaborativeQuestions:', result);
      
      if (result.data.success) {
        console.log(`✅ Generated ${result.data.questionsCount} questions`);
      } else {
        throw new Error('Failed to generate questions');
      }
    } catch (error) {
      console.error('❌ Error generating questions:', error);
      alert('Failed to generate questions. Please try again.');
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const handleRecordingComplete = async (audioBlob, audioUrl, questionId) => {
    if (!currentAuthor || !story) return;

    try {
      console.log(`🎤 Recording completed for question ${questionId} by ${currentAuthor.firstName}`);
      
      // Transcribe the audio
      const transcriptionResult = await fn.transcribeAudio({
        storyId: story.id,
        questionId: questionId,
        audioUrl: audioUrl,
        authorId: currentAuthor.userId || currentUser.uid,
        authorEmail: currentAuthor.email || currentUser.email,
        isCollaborative: true
      });

      if (transcriptionResult.data.success) {
        console.log('✅ Transcription completed for collaborative story');
      }
    } catch (error) {
      console.error('Error processing recording:', error);
      alert('Failed to process recording. Please try again.');
    }
  };

  // Calculate overall progress stats
  const calculateProgressStats = () => {
    if (!questions.length || !story || !currentAuthor) return null;
    
    const totalQuestions = questions.length;
    const totalPossibleAnswers = totalQuestions * 2; // 2 authors
    
    // Count answered by current user
    let currentUserAnswered = 0;
    // Count answered by other author(s)
    let otherAuthorsAnswered = 0;
    
    console.log('📊 Calculating progress stats:', { 
      totalQuestions, 
      currentAuthorEmail: currentAuthor?.email,
      questionAnswersKeys: Object.keys(questionAnswers).length 
    });
    
    questions.forEach(q => {
      const answers = questionAnswers[q.id] || [];
      
      // Find current user's answer
      const currentUserAnswer = answers.find(a => 
        (a.authorEmail && a.authorEmail.toLowerCase() === currentAuthor.email.toLowerCase()) ||
        (a.userEmail && a.userEmail.toLowerCase() === currentAuthor.email.toLowerCase()) ||
        (a.userId && currentAuthor.userId && a.userId === currentAuthor.userId) ||
        (a.userId && currentUser?.uid && a.userId === currentUser.uid)
      );
      
      if (currentUserAnswer) {
        currentUserAnswered++;
      }
      
      // Count other authors' answers
      const otherAnswers = answers.filter(a => {
        const isCurrentUser = 
          (a.authorEmail && a.authorEmail.toLowerCase() === currentAuthor.email.toLowerCase()) ||
          (a.userEmail && a.userEmail.toLowerCase() === currentAuthor.email.toLowerCase()) ||
          (a.userId && currentAuthor.userId && a.userId === currentAuthor.userId) ||
          (a.userId && currentUser?.uid && a.userId === currentUser.uid);
        return !isCurrentUser;
      });
      
      otherAuthorsAnswered += otherAnswers.length;
    });
    
    const totalAnswered = currentUserAnswered + otherAuthorsAnswered;
    const completionPercentage = Math.round((totalAnswered / totalPossibleAnswers) * 100);
    
    console.log('📊 Progress stats calculated:', {
      currentUserAnswered,
      otherAuthorsAnswered,
      totalAnswered,
      completionPercentage
    });
    
    return {
      totalQuestions,
      totalPossibleAnswers,
      totalAnswered,
      completionPercentage,
      currentUserAnswered,
      otherAuthorsAnswered
    };
  };
  
  const getQuestionProgress = (question) => {
    const answers = questionAnswers[question.id] || [];
    
    console.log('🔍 getQuestionProgress called for question:', question.id.substring(0, 8), {
      subcollectionAnswersCount: answers.length,
      hasCollaborativeProgress: !!question.collaborativeProgress,
      hasAuthorAnswers: !!question.authorAnswers
    });
    
    // PRIORITY 1: Use actual answers from subcollection (most reliable)
    if (answers.length > 0) {
      // Count unique authors
      const uniqueAuthors = new Set();
      answers.forEach(answer => {
        const email = answer.authorEmail || answer.userEmail || answer.userId;
        if (email) uniqueAuthors.add(email.toLowerCase());
      });
      console.log('📊 Using subcollection answers, unique authors:', uniqueAuthors.size);
      return { answered: uniqueAuthors.size, total: 2 };
    }
    
    // PRIORITY 2: Check if question uses new collaborative structure
    if (question.isCollaborative && question.collaborativeProgress) {
      return {
        answered: question.collaborativeProgress.answeredCount || 0,
        total: question.collaborativeProgress.totalAuthors || 2
      };
    }
    
    // PRIORITY 3: Check authorAnswers structure (newer collaborative format)
    if (question.authorAnswers) {
      const answeredCount = Object.values(question.authorAnswers).filter(answer => answer && answer.hasAnswer).length;
      console.log('📊 Using authorAnswers structure, count:', answeredCount);
      return { answered: answeredCount, total: 2 };
    }
    
    // PRIORITY 4: Legacy single-answer structure (least reliable for collaborative)
    if (question.hasAnswer && question.lastAnswerId) {
      console.log('📊 Using legacy hasAnswer structure, treating as 1 answer');
      return { answered: 1, total: 2 };
    }
    
    // No answers yet
    return { answered: 0, total: 2 };
  };

  const getAuthorAnswerForQuestion = (question, authorEmail) => {
    console.log('🔍 getAuthorAnswerForQuestion called:', {
      questionId: question.id,
      authorEmail,
      hasAuthorAnswers: !!question.authorAnswers,
      authorAnswers: question.authorAnswers,
      hasSubcollectionAnswers: !!questionAnswers[question.id]
    });
    
    // PRIORITY 1: Check answers subcollection (has full transcript data)
    const answers = questionAnswers[question.id] || [];
    console.log('� Searching subcollection answers:', {
      questionId: question.id,
      authorEmail,
      totalAnswers: answers.length,
      answers: answers.map(a => ({ 
        id: a.id, 
        authorEmail: a.authorEmail, 
        userEmail: a.userEmail,
        userId: a.userId,
        hasTranscript: !!(a.cleanedTranscription || a.rawTranscription)
      }))
    });
    
    const subcollectionAnswer = answers.find(a => {
      const emailMatch = 
        (a.authorEmail && authorEmail && a.authorEmail.toLowerCase() === authorEmail.toLowerCase()) ||
        (a.userEmail && authorEmail && a.userEmail.toLowerCase() === authorEmail.toLowerCase());
      
      console.log('🔍 Checking answer:', {
        answerId: a.id,
        answerAuthorEmail: a.authorEmail,
        answerUserEmail: a.userEmail,
        lookingFor: authorEmail,
        emailMatch
      });
      
      return emailMatch;
    });
    
    if (subcollectionAnswer) {
      console.log('📝 Found answer in subcollection with transcript:', subcollectionAnswer.id);
      return {
        id: subcollectionAnswer.id,
        transcript: subcollectionAnswer.cleanedTranscription || subcollectionAnswer.rawTranscription || "No transcription available",
        cleanedTranscription: subcollectionAnswer.cleanedTranscription,
        rawTranscription: subcollectionAnswer.rawTranscription,
        audioUrl: subcollectionAnswer.audioUrl,
        answeredAt: subcollectionAnswer.createdAt || subcollectionAnswer.answeredAt,
        userId: subcollectionAnswer.userId
      };
    }
    
    // PRIORITY 2: Check authorAnswers structure (metadata only, use as fallback)
    if (question.authorAnswers) {
      const authorAnswer = question.authorAnswers[authorEmail];
      console.log('📝 Found authorAnswer metadata:', authorAnswer);
      if (authorAnswer && authorAnswer.hasAnswer) {
        return {
          id: authorAnswer.answerId,
          transcript: "Loading transcript...",
          audioUrl: authorAnswer.audioUrl,
          answeredAt: authorAnswer.answeredAt,
          userId: authorAnswer.userId
        };
      }
      return null;
    }
    
    // Legacy structure support (answers as object on question)
    const legacyAnswer = question.answers?.[authorEmail];
    if (legacyAnswer) {
      console.log('📝 Found legacy answer on question object');
      return legacyAnswer;
    }
    
    console.log('📝 No answer found for author:', authorEmail);
    return null;
  };

  const canUserRecordQuestion = (question) => {
    if (userRole !== 'author' || !currentAuthor) return false;
    
    // Authors can always record or re-record their own answers
    return true;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading collaborative story...</p>
          {retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Retry attempt {retryCount}/5 - Story may have just been created
            </p>
          )}
          {error && (
            <p className="text-sm text-blue-600 mt-2">{error}</p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600">Story not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="text-gray-600 hover:text-gray-900 flex items-center mb-4 font-medium"
          style={{ color: 'var(--ms-primary)' }}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Combined Story Header and Progress */}
      <div className="memory-stream-card mb-6">
        <StoryHeader story={story} />
        
        {/* Progress Dashboard */}
        {questions.length > 0 && (() => {
          const stats = calculateProgressStats();
          if (!stats) return null;
          
          return (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Story Progress</h3>
                <span className="text-sm text-gray-600">
                  {stats.totalAnswered} of {stats.totalPossibleAnswers} answers
                </span>
              </div>
              
              {/* Combined Progress Bar */}
              <div className="mb-4">
                <div className="bg-gray-200 rounded-full h-3">
                  <div 
                    className="h-3 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${stats.completionPercentage}%`,
                      background: 'linear-gradient(90deg, var(--ms-secondary) 0%, var(--ms-accent-green) 100%)'
                    }}
                  ></div>
                </div>
                <div className="text-center mt-2">
                  <span className="text-2xl font-bold" style={{ color: 'var(--ms-primary)' }}>
                    {stats.completionPercentage}%
                  </span>
                  <span className="text-sm text-gray-600 ml-2">complete</span>
                </div>
              </div>
              
              {/* Individual Progress */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Your Progress</div>
                  <div className="text-2xl font-bold" style={{ color: 'var(--ms-secondary)' }}>
                    {stats.currentUserAnswered}
                  </div>
                  <div className="text-xs text-gray-500">of {stats.totalQuestions} questions</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Co-Author Progress</div>
                  <div className="text-2xl font-bold" style={{ color: 'var(--ms-accent-green)' }}>
                    {stats.otherAuthorsAnswered}
                  </div>
                  <div className="text-xs text-gray-500">of {stats.totalQuestions} questions</div>
                </div>
              </div>
              
              {/* Encouragement Message */}
              {stats.completionPercentage >= 50 && stats.completionPercentage < 100 && (
                <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
                  <p className="text-sm text-center" style={{ color: '#065F46' }}>
                    🎉 Great progress! You're more than halfway there!
                  </p>
                </div>
              )}
              {stats.completionPercentage === 100 && (
                <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
                  <p className="text-sm text-center font-medium" style={{ color: '#065F46' }}>
                    ✨ Amazing! Both authors have completed all questions!
                  </p>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Questions Generation */}
      {questions.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {generatingQuestions ? (
            <div>
              <div 
                className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                style={{ borderColor: 'var(--ms-secondary)' }}
              ></div>
              <p className="text-gray-600">Generating personalized questions...</p>
            </div>
          ) : (
            <div>
              <div className="text-4xl mb-4">🤖</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Generate Questions</h3>
              <p className="text-gray-600 mb-6">
                We'll create personalized questions for your collaborative story
              </p>
              <button
                onClick={handleGenerateQuestions}
                className="memory-stream-button"
              >
                Generate Questions
              </button>
            </div>
          )}
        </div>
      )}

      {/* Questions List - Simple Clickable Cards */}
      {questions.length > 0 && (
        <div className="space-y-4">
          {questions.map((question, index) => {
            const progress = getQuestionProgress(question);
            const userAnswer = getAuthorAnswerForQuestion(question, currentAuthor?.email);
            
            // Get other author emails
            const otherAuthorEmails = question.authorAnswers 
              ? Object.keys(question.authorAnswers).filter(email => 
                  email.toLowerCase() !== currentAuthor?.email?.toLowerCase()
                )
              : [];
            const otherAuthorAnswered = otherAuthorEmails.some(email => 
              getAuthorAnswerForQuestion(question, email)
            );
            
            return (
              <div 
                key={question.id} 
                className="bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-[var(--ms-secondary)] transition-all cursor-pointer overflow-hidden"
                onClick={() => navigate(`/collaborative-story/${story.id}/question/${question.id}`)}
              >
                <div className="p-5 lg:p-6">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <span 
                        className="px-4 py-1.5 rounded-full text-base font-semibold text-white flex-shrink-0"
                        style={{ backgroundColor: 'var(--ms-secondary)' }}
                      >
                        Q{index + 1}
                      </span>
                      
                      {/* Status dots for both authors */}
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: userAnswer ? '#10B981' : '#E5E7EB',
                            borderColor: userAnswer ? '#059669' : '#9CA3AF'
                          }}
                          title={`${currentAuthor?.name || 'You'}: ${userAnswer ? 'Answered' : 'Not answered'}`}
                        />
                        <div 
                          className="w-4 h-4 rounded-full border-2"
                          style={{ 
                            backgroundColor: otherAuthorAnswered ? '#10B981' : '#E5E7EB',
                            borderColor: otherAuthorAnswered ? '#059669' : '#9CA3AF'
                          }}
                          title={`Co-Author: ${otherAuthorAnswered ? 'Answered' : 'Not answered'}`}
                        />
                      </div>
                    </div>
                    
                    {/* Completion badge */}
                    {progress.answered === progress.total && (
                      <span className="text-sm px-3 py-1 rounded-full font-medium bg-green-100 text-green-800 flex-shrink-0">
                        ✓ Complete
                      </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-2">
                    {question.questionText || question.text || 'Question text not available'}
                  </h3>
                  
                  {/* Progress text */}
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-1">📊</span>
                    <span>{progress.answered} of {progress.total} answered</span>
                  </div>
                </div>
                
                {/* Click indicator */}
                <div className="bg-gray-50 px-5 py-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600 font-medium flex items-center">
                    <span className="mr-2">→</span>
                    Click to {userAnswer ? 'view or re-record' : 'record'} your answer
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
